import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { QcIncidentNotesThread } from "@/components/qc/QcIncidentNotesThread";
import { QcIncidentResolvePanel } from "@/components/qc/QcIncidentResolvePanel";
import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { CardTitleWithHelp, PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { QC_INCIDENT_HUB_MODULE_KEYS } from "@/lib/qc/qcIncidentNav";
import { useHubModuleWrite, useHubModuleWriteAny } from "@/lib/useHubModuleWrite";
import { EM_DASH } from "@/lib/utils";
import { SkuDocumentsTab } from "@/pages/mrp/SkuDocumentsTab";
import {
  PCBA_PRODUCT_FAMILY_OPTIONS,
  QC_INCIDENTS_QUERY_KEY,
  QC_ROOT_CAUSES_QUERY_KEY,
  fetchIncident,
  fetchRootCauses,
  type QcIncident,
  type QcIncidentDetail,
} from "@/pages/manufacturing/qcIncidentsApi";

import { TierReviewStageBadge } from "./QualityTierReviewQueuePage";
import { analysisTransfersQueryKey, fetchAnalysisTransfers, type AnalysisTransfer } from "./analysisTransfersApi";
import { QualityAnalysisTransferPanel } from "./QualityAnalysisTransferPanel";
import { QualityNcrDeviceWorkPanel } from "./QualityNcrDeviceWork";
import { createQualityCase, linkQualityCaseIncident, qualityCaseLabel } from "./casesApi";
import { correctiveActionSourceLabel } from "./correctiveActionsApi";
import {
  addQualityNcrUnitReading,
  createQualityRcaForNcr,
  fetchQualityNcr,
  linkQualityRcaToNcr,
  qualityEventLabel,
  qualityEventStatusLabel,
  qualityNcrLabel,
  qualityNcrQueryKey,
  type QualityNcr,
  type QualityNcrEventLink,
  type QualityNcrRmaArtifact,
  type QualityNcrUnitReading,
} from "./qualityEventNcrApi";

const DETAIL_HELP =
  "An NCR is one unit's quality incident. The event is the shared reason. Readings stay on this NCR. An RCA can be created here or linked when several NCRs name the same failure.";

function formatIssueDate(value: string | null): string {
  if (value == null || value.trim() === "") return EM_DASH;
  const parsed = new Date(`${value.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function holdRelease(incident: QcIncident): { label: "Hold" | "Released"; tone: BadgeTone } {
  if (incident.repairScrap === "scrap" || incident.status !== "resolved") {
    return { label: "Hold", tone: "warning" };
  }
  return { label: "Released", tone: "success" };
}

function productFamilyLabel(value: QcIncident["pcbaProductFamily"]): string {
  if (value == null) return EM_DASH;
  return PCBA_PRODUCT_FAMILY_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : error instanceof Error ? error.message : fallback;
}

function IdentityField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="font-mono text-sm text-slate-900">{value}</dd>
    </div>
  );
}

function presentText(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed === "" ? null : trimmed;
}

function serialLookupPath(serial: string): string {
  return `/manufacturing/device-lookup?serial=${encodeURIComponent(serial)}`;
}

function sourceLine(event: QualityNcrEventLink): string {
  const category =
    event.sourceCategory != null
      ? correctiveActionSourceLabel(event.sourceCategory)
      : (event.source?.trim() ?? "");
  const party = event.supplierName?.trim() ?? "";
  if (category !== "" && party !== "") return `${category} - ${party}`;
  if (category !== "") return category;
  if (party !== "") return party;
  return EM_DASH;
}

function loanStatusText(rows: AnalysisTransfer[]): string | null {
  if (rows.length === 0) return null;
  const openLoan = rows.find((row) => row.returnedAt == null);
  if (openLoan != null) {
    const destination = openLoan.destinationName?.trim() ?? "";
    return destination === "" ? "Out" : `Out to ${destination}`;
  }
  return "Returned";
}

function ReadingFields({
  reading,
}: {
  reading: {
    unitSerial: string | null;
    unitModel: string | null;
    aaVoltage: string | null;
    cVoltage: string | null;
    hybridCap: string | null;
    firmwareVersion: string | null;
    boardRevision: string | null;
    visualInspection: string | null;
    boxLabel: string | null;
    loggedBy: string | null;
    receivedAt: string | null;
  };
}) {
  return (
    <div className="space-y-3 rounded-md border border-slate-200 px-3 py-3">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Battery readings</p>
        <dl className="mt-2 grid gap-3 sm:grid-cols-3">
          <IdentityField label="AA voltage" value={reading.aaVoltage ?? EM_DASH} />
          <IdentityField label="C voltage" value={reading.cVoltage ?? EM_DASH} />
          <IdentityField label="Hybrid cap" value={reading.hybridCap ?? EM_DASH} />
        </dl>
      </div>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Log dump</p>
        <p className="mt-1 whitespace-pre-wrap text-sm text-slate-900">{reading.visualInspection?.trim() || EM_DASH}</p>
      </div>
      <dl className="grid gap-3 sm:grid-cols-3">
        <IdentityField label="Serial" value={reading.unitSerial ?? EM_DASH} />
        <IdentityField label="Model" value={reading.unitModel ?? EM_DASH} />
        <IdentityField label="Firmware" value={reading.firmwareVersion ?? EM_DASH} />
        <IdentityField label="Board revision" value={reading.boardRevision ?? EM_DASH} />
        <IdentityField label="Box label" value={reading.boxLabel ?? EM_DASH} />
        <IdentityField label="Logged by" value={reading.loggedBy ?? EM_DASH} />
        <IdentityField label="Received" value={formatIssueDate(reading.receivedAt)} />
      </dl>
    </div>
  );
}

function artifactReading(artifact: QualityNcrRmaArtifact) {
  const logText = [artifact.visualInspection?.trim(), artifact.rcaNotes?.trim()]
    .filter((value): value is string => value != null && value !== "")
    .join("\n\n");
  return {
    unitSerial: artifact.unitSerial,
    unitModel: artifact.unitModel,
    aaVoltage: artifact.aaVoltage,
    cVoltage: artifact.cVoltage,
    hybridCap: artifact.hybridCap,
    firmwareVersion: artifact.firmwareVersion,
    boardRevision: artifact.boardRevision,
    visualInspection: logText === "" ? null : logText,
    boxLabel: artifact.boxLabel,
    loggedBy: artifact.loggedBy,
    receivedAt: artifact.receivedAt,
  };
}

function storedReading(reading: QualityNcrUnitReading) {
  return {
    unitSerial: reading.unitSerial,
    unitModel: reading.unitModel,
    aaVoltage: reading.aaVoltage,
    cVoltage: reading.cVoltage,
    hybridCap: reading.hybridCap,
    firmwareVersion: reading.firmwareVersion,
    boardRevision: reading.boardRevision,
    visualInspection: reading.visualInspection,
    boxLabel: reading.boxLabel,
    loggedBy: reading.loggedBy,
    receivedAt: reading.receivedAt,
  };
}

function QualityNcrRcaCard({ ncr, writesAllowed }: { ncr: QualityNcr; writesAllowed: boolean }) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<"create" | "link" | null>(null);
  const [problemStatement, setProblemStatement] = useState("");
  const [linkId, setLinkId] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: qualityNcrQueryKey(ncr.id) });
  };

  const createMutation = useMutation({
    mutationFn: () => createQualityRcaForNcr(ncr.id, problemStatement.trim() || null),
    onSuccess: async () => {
      setFormError(null);
      setProblemStatement("");
      setMode(null);
      await refresh();
    },
    onError: (error: unknown) => setFormError(errorMessage(error, "Could not create the RCA.")),
  });

  const linkMutation = useMutation({
    mutationFn: (rcaId: number) => linkQualityRcaToNcr(rcaId, ncr.id),
    onSuccess: async () => {
      setFormError(null);
      setLinkId("");
      setMode(null);
      await refresh();
    },
    onError: (error: unknown) => setFormError(errorMessage(error, "Could not link the RCA.")),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint="Create a new RCA for this NCR, or link an RCA that already exists. Several NCRs can share one RCA.">
          RCA
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody className="space-y-4">
        {ncr.rcas.length === 0 ? (
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" disabled={!writesAllowed} onClick={() => setMode("create")}>
              Create RCA
            </Button>
            <Button type="button" size="sm" variant="secondary" disabled={!writesAllowed} onClick={() => setMode("link")}>
              Link RCA
            </Button>
          </div>
        ) : (
          <ul className="space-y-2 text-sm">
            {ncr.rcas.map((rca) => (
              <li key={rca.id}>
                <Link className="font-medium text-sky-700 hover:underline" to={`/quality/rcas/${rca.id}`}>
                  RCA {rca.id}
                </Link>
                <span className="text-slate-700"> · {rca.status === "closed" ? "Closed" : "Open"}</span>
              </li>
            ))}
          </ul>
        )}
        {writesAllowed && ncr.rcas.length === 0 && mode === "create" ? (
          <form
            className="space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              setFormError(null);
              createMutation.mutate();
            }}
          >
            <Label htmlFor="ncr-rca-problem">Problem statement</Label>
            <TextArea
              id="ncr-rca-problem"
              value={problemStatement}
              onChange={(event) => setProblemStatement(event.target.value)}
              className="font-sans"
            />
            <Button type="submit" isLoading={createMutation.isPending}>
              Create RCA
            </Button>
          </form>
        ) : null}
        {writesAllowed && ncr.rcas.length === 0 && mode === "link" ? (
          <form
            className="space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              const rcaId = Number(linkId.trim());
              if (!Number.isInteger(rcaId) || rcaId < 1) {
                setFormError("Enter the RCA number to link.");
                return;
              }
              setFormError(null);
              linkMutation.mutate(rcaId);
            }}
          >
            <Label htmlFor="ncr-rca-link">Existing RCA number</Label>
            <Input
              id="ncr-rca-link"
              value={linkId}
              onChange={(event) => setLinkId(event.target.value)}
              inputMode="numeric"
            />
            <Button type="submit" variant="secondary" isLoading={linkMutation.isPending}>
              Link RCA
            </Button>
          </form>
        ) : null}
        {formError ? <p className="text-sm text-red-700">{formError}</p> : null}
      </CardBody>
    </Card>
  );
}

function NcrTierStage({ incidentId }: { incidentId: number }) {
  const stage = useQuery({
    queryKey: ["quality", "tier-review", String(incidentId)],
    queryFn: () => apiFetch<{ stage: string; reviewPath: string }>(`/api/quality/tier-reviews/${incidentId}`),
  });
  const value = stage.data?.stage;
  if (value == null || stage.data == null) return null;
  return (
    <Link to={stage.data.reviewPath}>
      <TierReviewStageBadge stage={value} />
    </Link>
  );
}

export function QualityNcrDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const ncrId = Number(id);
  const qualityWritesAllowed = useHubModuleWrite("quality");
  const incidentWritesAllowed = useHubModuleWriteAny(QC_INCIDENT_HUB_MODULE_KEYS);
  const enabled = Number.isFinite(ncrId) && ncrId > 0;
  const [caseTitle, setCaseTitle] = useState("");
  const [caseLinkId, setCaseLinkId] = useState("");
  const [caseError, setCaseError] = useState<string | null>(null);
  const [showLinkForms, setShowLinkForms] = useState(false);
  const [showLoan, setShowLoan] = useState(false);
  const [showReadingForm, setShowReadingForm] = useState(false);
  const [showDeviceWork, setShowDeviceWork] = useState(false);
  const [readingError, setReadingError] = useState<string | null>(null);
  const [aaVoltage, setAaVoltage] = useState("");
  const [cVoltage, setCVoltage] = useState("");
  const [hybridCap, setHybridCap] = useState("");
  const [firmwareVersion, setFirmwareVersion] = useState("");
  const [boardRevision, setBoardRevision] = useState("");
  const [unitModel, setUnitModel] = useState("");
  const [boxLabel, setBoxLabel] = useState("");
  const [visualInspection, setVisualInspection] = useState("");

  const ncrQuery = useQuery({
    queryKey: qualityNcrQueryKey(ncrId),
    queryFn: () => fetchQualityNcr(ncrId),
    enabled,
  });
  const incidentQuery = useQuery({
    queryKey: [...QC_INCIDENTS_QUERY_KEY, "detail", ncrId],
    queryFn: () => fetchIncident(ncrId),
    enabled,
  });
  const rootCausesQuery = useQuery({
    queryKey: QC_ROOT_CAUSES_QUERY_KEY,
    queryFn: fetchRootCauses,
    enabled: incidentQuery.data != null,
  });
  const loanQuery = useQuery({
    queryKey: analysisTransfersQueryKey({ qualityIncidentId: ncrId }),
    queryFn: () => fetchAnalysisTransfers({ qualityIncidentId: ncrId }),
    enabled,
  });

  const createCaseMutation = useMutation({
    mutationFn: () =>
      createQualityCase({
        title: caseTitle.trim(),
        sourceType: "qc_incident",
        qualityIncidentIds: [ncrId],
      }),
    onSuccess: async () => {
      setCaseError(null);
      setCaseTitle("");
      setShowLinkForms(false);
      await queryClient.invalidateQueries({ queryKey: qualityNcrQueryKey(ncrId) });
    },
    onError: (error: unknown) => setCaseError(errorMessage(error, "Could not create the quality case.")),
  });

  const linkCaseMutation = useMutation({
    mutationFn: (caseId: number) => linkQualityCaseIncident(caseId, ncrId),
    onSuccess: async () => {
      setCaseError(null);
      setCaseLinkId("");
      setShowLinkForms(false);
      await queryClient.invalidateQueries({ queryKey: qualityNcrQueryKey(ncrId) });
    },
    onError: (error: unknown) => setCaseError(errorMessage(error, "Could not link the quality case.")),
  });

  const readingMutation = useMutation({
    mutationFn: () =>
      addQualityNcrUnitReading(ncrId, {
        aaVoltage,
        cVoltage,
        hybridCap,
        firmwareVersion,
        boardRevision,
        unitModel,
        boxLabel,
        visualInspection,
        unitSerial: incidentQuery.data?.pcbaSerial ?? ncrQuery.data?.pcbaSerial ?? "",
      }),
    onSuccess: async () => {
      setReadingError(null);
      setAaVoltage("");
      setCVoltage("");
      setHybridCap("");
      setFirmwareVersion("");
      setBoardRevision("");
      setUnitModel("");
      setBoxLabel("");
      setVisualInspection("");
      setShowReadingForm(false);
      await queryClient.invalidateQueries({ queryKey: qualityNcrQueryKey(ncrId) });
    },
    onError: (error: unknown) => setReadingError(errorMessage(error, "Could not save the reading.")),
  });

  if (!enabled) {
    return <p className="text-sm text-red-700">NCR was not found.</p>;
  }
  if (ncrQuery.isError) {
    return <p className="text-sm text-red-700">{(ncrQuery.error as Error).message}</p>;
  }
  if (ncrQuery.isLoading || ncrQuery.data == null) {
    return <p className="text-sm text-slate-600">Loading NCR…</p>;
  }

  const ncr = ncrQuery.data;
  const incident: QcIncidentDetail | undefined = incidentQuery.data;
  const caseIds: number[] = [];
  if (ncr.qualityCaseId != null) caseIds.push(ncr.qualityCaseId);
  if (ncr.event?.qualityCaseId != null && !caseIds.includes(ncr.event.qualityCaseId)) {
    caseIds.push(ncr.event.qualityCaseId);
  }
  const hold = incident == null ? null : holdRelease(incident);
  const serials = [
    { kind: "Device", number: presentText(incident?.deviceLabelSerial ?? ncr.deviceLabelSerial) },
    { kind: "Clamp", number: presentText(incident?.clampSerial ?? ncr.clampSerial) },
    { kind: "PCBA", number: presentText(incident?.pcbaSerial ?? ncr.pcbaSerial) },
  ].filter((row): row is { kind: string; number: string } => row.number != null);
  const loanText = loanStatusText(loanQuery.data?.rows ?? []);
  const documentSku = presentText(ncr.productSku) ?? presentText(ncr.finishedSku);
  const linkItems: Array<{ key: string; label: string; to: string }> = [];
  if (ncr.event != null) {
    linkItems.push({
      key: `event-${ncr.event.id}`,
      label: qualityEventLabel(ncr.event.id),
      to: `/quality/events/${ncr.event.id}`,
    });
  }
  for (const caseId of caseIds) {
    linkItems.push({
      key: `case-${caseId}`,
      label: qualityCaseLabel(caseId),
      to: `/quality/cases/${caseId}`,
    });
  }
  const seenRmas = new Set<number>();
  for (const artifact of ncr.rmaArtifacts) {
    if (seenRmas.has(artifact.rmaId)) continue;
    seenRmas.add(artifact.rmaId);
    linkItems.push({
      key: `rma-${artifact.rmaId}`,
      label: artifact.rmaNumber ?? `RMA ${artifact.rmaId}`,
      to: `/quality/rmas/${artifact.rmaId}`,
    });
  }
  for (const rca of ncr.rcas) {
    linkItems.push({
      key: `rca-${rca.id}`,
      label: `RCA ${rca.id}`,
      to: `/quality/rcas/${rca.id}`,
    });
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={DETAIL_HELP} breadcrumbLabel={qualityNcrLabel(ncr.id)}>
          {qualityNcrLabel(ncr.id)}
        </PageTitleWithHelp>
        <NcrTierStage incidentId={ncr.id} />
        <Button type="button" variant="secondary" onClick={() => setShowLoan(true)}>
          Analysis loan
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitleWithHelp hoverHint="Serial identity stays on the NCR. Hold means the open incident or a scrap disposition still blocks the unit.">
            Identity
          </CardTitleWithHelp>
        </CardHeader>
        <CardBody>
          <dl className="grid gap-3 sm:grid-cols-3">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Hold / release</dt>
              <dd className="text-sm text-slate-900">
                {hold == null ? EM_DASH : <Badge tone={hold.tone}>{hold.label}</Badge>}
              </dd>
            </div>
            <IdentityField label="Product family" value={productFamilyLabel(incident?.pcbaProductFamily ?? null)} />
            <IdentityField label="TLA variant" value={incident?.pcbaTlaVariant ?? EM_DASH} />
            {serials.length === 0 ? null : (
              <div className="sm:col-span-3">
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Serial</dt>
                <dd className="space-y-1 text-sm text-slate-900">
                  {serials.map((serial) => (
                    <p key={`${serial.kind}-${serial.number}`}>
                      <Link className="font-mono text-sky-700 hover:underline" to={serialLookupPath(serial.number)}>
                        {serial.kind} / {serial.number}
                      </Link>
                    </p>
                  ))}
                </dd>
              </div>
            )}
            {loanText == null ? null : (
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Analysis loan</dt>
                <dd className="text-sm text-slate-900">{loanText}</dd>
              </div>
            )}
          </dl>
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="flex flex-wrap items-center justify-between gap-2">
          <CardTitleWithHelp hoverHint="The quality event linked to this NCR. Source is the category and the linked party.">
            Event
          </CardTitleWithHelp>
          {ncr.event == null ? null : (
            <Link className="text-sm text-sky-700 hover:underline" to={`/quality/events/${ncr.event.id}`}>
              {qualityEventLabel(ncr.event.id)}
            </Link>
          )}
        </CardHeader>
        <CardBody>
          {ncr.event == null ? (
            <p className="text-sm text-slate-600">This NCR is not linked to a quality event.</p>
          ) : (
            <dl className="grid gap-3 md:grid-cols-2">
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Status</dt>
                <dd className="text-sm text-slate-900">{qualityEventStatusLabel(ncr.event.status)}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Source</dt>
                <dd className="text-sm text-slate-900">{sourceLine(ncr.event)}</dd>
              </div>
              <div className="md:col-span-2">
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Summary</dt>
                <dd className="whitespace-pre-wrap text-sm text-slate-900">{ncr.event.summary ?? EM_DASH}</dd>
              </div>
            </dl>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="flex flex-wrap items-center justify-between gap-2">
          <CardTitleWithHelp hoverHint="Battery readings, log dumps, and the other per-unit fields for this NCR. An RMA is not required.">
            Unit readings
          </CardTitleWithHelp>
          {qualityWritesAllowed && !showReadingForm ? (
            <Button type="button" size="sm" variant="secondary" onClick={() => setShowReadingForm(true)}>
              Add reading
            </Button>
          ) : null}
        </CardHeader>
        <CardBody className="space-y-3">
          {ncr.rmaArtifacts.map((artifact) => (
            <ReadingFields
              key={`${artifact.rmaId}:${artifact.intakeId ?? artifact.unitSerial ?? "device"}`}
              reading={artifactReading(artifact)}
            />
          ))}
          {ncr.unitReadings.map((reading) => (
            <ReadingFields key={reading.id} reading={storedReading(reading)} />
          ))}
          {showReadingForm ? (
            <form
              className="space-y-3"
              onSubmit={(event) => {
                event.preventDefault();
                const filled = [aaVoltage, cVoltage, hybridCap, firmwareVersion, boardRevision, unitModel, boxLabel, visualInspection].some(
                  (value) => value.trim() !== "",
                );
                if (!filled) {
                  setReadingError("Enter a reading.");
                  return;
                }
                setReadingError(null);
                readingMutation.mutate();
              }}
            >
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <Label htmlFor="ncr-aa-voltage">AA voltage</Label>
                  <Input id="ncr-aa-voltage" value={aaVoltage} onChange={(event) => setAaVoltage(event.target.value)} />
                </div>
                <div>
                  <Label htmlFor="ncr-c-voltage">C voltage</Label>
                  <Input id="ncr-c-voltage" value={cVoltage} onChange={(event) => setCVoltage(event.target.value)} />
                </div>
                <div>
                  <Label htmlFor="ncr-hybrid-cap">Hybrid cap</Label>
                  <Input id="ncr-hybrid-cap" value={hybridCap} onChange={(event) => setHybridCap(event.target.value)} />
                </div>
                <div>
                  <Label htmlFor="ncr-firmware">Firmware</Label>
                  <Input id="ncr-firmware" value={firmwareVersion} onChange={(event) => setFirmwareVersion(event.target.value)} />
                </div>
                <div>
                  <Label htmlFor="ncr-board">Board revision</Label>
                  <Input id="ncr-board" value={boardRevision} onChange={(event) => setBoardRevision(event.target.value)} />
                </div>
                <div>
                  <Label htmlFor="ncr-model">Model</Label>
                  <Input id="ncr-model" value={unitModel} onChange={(event) => setUnitModel(event.target.value)} />
                </div>
                <div>
                  <Label htmlFor="ncr-box">Box label</Label>
                  <Input id="ncr-box" value={boxLabel} onChange={(event) => setBoxLabel(event.target.value)} />
                </div>
              </div>
              <div>
                <Label htmlFor="ncr-log">Log dump</Label>
                <TextArea
                  id="ncr-log"
                  value={visualInspection}
                  onChange={(event) => setVisualInspection(event.target.value)}
                  className="font-sans"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="submit" isLoading={readingMutation.isPending}>
                  Save reading
                </Button>
                <Button type="button" variant="secondary" onClick={() => setShowReadingForm(false)}>
                  Cancel
                </Button>
              </div>
              {readingError ? <p className="text-sm text-red-700">{readingError}</p> : null}
            </form>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="flex flex-wrap items-center justify-between gap-2">
          <CardTitleWithHelp hoverHint="Investigation worksheet for this unit: board SKU, field dates, battery, intake diagnostics, photos, and data log windows. The RCA reads this; it is stored on the NCR.">
            Device worksheet
          </CardTitleWithHelp>
          <Button type="button" size="sm" variant="secondary" onClick={() => setShowDeviceWork((current) => !current)}>
            {showDeviceWork ? "Hide" : "Show"}
          </Button>
        </CardHeader>
        {showDeviceWork ? (
          <CardBody>
            <QualityNcrDeviceWorkPanel ncrId={ncr.id} seed={null} writesAllowed={qualityWritesAllowed} />
          </CardBody>
        ) : null}
      </Card>

      <Card>
        <CardHeader>
          <CardTitleWithHelp hoverHint="Notes for people working this NCR.">Notes</CardTitleWithHelp>
        </CardHeader>
        <CardBody>
          <QcIncidentNotesThread incidentId={ncr.id} writesAllowed={incidentWritesAllowed} />
        </CardBody>
      </Card>

      {incidentQuery.isLoading ? <p className="text-sm text-slate-600">Loading the QC incident…</p> : null}
      {incidentQuery.isError ? (
        <p className="text-sm text-red-700">{(incidentQuery.error as Error).message}</p>
      ) : null}
      {incident == null && !incidentQuery.isLoading && !incidentQuery.isError ? (
        <p className="text-sm text-slate-600">The manufacturing QC incident record was not found for this NCR.</p>
      ) : null}
      {incident != null ? (
        <QcIncidentResolvePanel
          incident={incident}
          rootCauses={rootCausesQuery.data?.rows ?? []}
          writesAllowed={incidentWritesAllowed}
          omitSummary
          omitNotes
          onClose={() => navigate("/quality/ncrs")}
          onResolved={() => {
            void incidentQuery.refetch();
          }}
        />
      ) : null}

      {showLoan ? (
        <Card>
          <CardHeader>
            <CardTitleWithHelp hoverHint="Send this unit out for analysis. The loan stays linked to this NCR.">
              Analysis loan
            </CardTitleWithHelp>
          </CardHeader>
          <CardBody>
            <QualityAnalysisTransferPanel
              embedded
              hideHeading
              parent={{ qualityIncidentId: ncr.id }}
              defaultSerial={incident?.deviceLabelSerial ?? incident?.pcbaSerial ?? ncr.pcbaSerial ?? ""}
            />
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitleWithHelp hoverHint="Controlled documents for the product resolved from this serial, including schematics.">
            Product documents
          </CardTitleWithHelp>
        </CardHeader>
        <CardBody className="space-y-3">
          {documentSku == null ? (
            <p className="text-sm text-slate-600">
              No product SKU was found for this serial in the Kodiak prefix assignment or on the serial record.
            </p>
          ) : (
            <>
              <p className="text-sm text-slate-900">
                Product <span className="font-mono">{documentSku}</span>
              </p>
              <SkuDocumentsTab sku={documentSku} />
            </>
          )}
        </CardBody>
      </Card>

      <QualityNcrRcaCard ncr={ncr} writesAllowed={qualityWritesAllowed} />

      <Card>
        <CardHeader className="flex flex-wrap items-center justify-between gap-2">
          <CardTitleWithHelp hoverHint="Linked quality records for this NCR.">Links</CardTitleWithHelp>
          {qualityWritesAllowed && !showLinkForms ? (
            <Button type="button" size="sm" variant="secondary" onClick={() => setShowLinkForms(true)}>
              Link or create
            </Button>
          ) : null}
        </CardHeader>
        <CardBody className="space-y-4 text-sm">
          {linkItems.length === 0 ? (
            <p className="text-slate-600">Nothing is linked.</p>
          ) : (
            <p className="text-slate-900">
              {linkItems.map((item, index) => (
                <span key={item.key}>
                  {index > 0 ? ", " : null}
                  <Link className="text-sky-700 hover:underline" to={item.to}>
                    {item.label}
                  </Link>
                </span>
              ))}
            </p>
          )}
          {qualityWritesAllowed && showLinkForms ? (
            <div className="grid gap-4 lg:grid-cols-2">
              <form
                className="space-y-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (caseTitle.trim() === "") {
                    setCaseError("Enter a title to create the quality case.");
                    return;
                  }
                  setCaseError(null);
                  createCaseMutation.mutate();
                }}
              >
                <Label htmlFor="ncr-case-title">New quality case title</Label>
                <Input id="ncr-case-title" value={caseTitle} onChange={(event) => setCaseTitle(event.target.value)} />
                <Button type="submit" isLoading={createCaseMutation.isPending}>
                  Create quality case
                </Button>
              </form>
              <form
                className="space-y-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  const caseId = Number(caseLinkId.trim());
                  if (!Number.isInteger(caseId) || caseId < 1) {
                    setCaseError("Enter the quality case number to link.");
                    return;
                  }
                  setCaseError(null);
                  linkCaseMutation.mutate(caseId);
                }}
              >
                <Label htmlFor="ncr-case-link">Existing quality case number</Label>
                <Input
                  id="ncr-case-link"
                  value={caseLinkId}
                  onChange={(event) => setCaseLinkId(event.target.value)}
                  inputMode="numeric"
                />
                <Button type="submit" variant="secondary" isLoading={linkCaseMutation.isPending}>
                  Link quality case
                </Button>
              </form>
            </div>
          ) : null}
          {caseError ? <p className="text-sm text-red-700">{caseError}</p> : null}
        </CardBody>
      </Card>
    </div>
  );
}
